<template>
  <div id="dialog-goal-edit" class="dialog">
    <span class="cls" @click="editDialogSwitch(false)"></span>
    <div class="paper">
      <!-- 상단: 진행 바 (신규일 때만) -->
      <div class="progress-bar" v-if="!isEdit">
        <div class="fill" :style="{ width: ((step + 1) / totalSteps) * 100 + '%' }"></div>
      </div>

      <!-- 수정 모드 헤더 -->
      <div class="edit-header" v-if="isEdit">
        <h2>목표 수정</h2>
      </div>

      <!-- 닫기 -->
      <button class="close" @click="editDialogSwitch(false)">
        <i class="ph-bold ph-x"></i>
      </button>

      <!-- 신규 모드: 단계별 -->
      <div class="content" v-if="!isEdit">
        <transition name="fade" mode="out-in">
          <!-- Step 0: 무엇을? -->
          <div v-if="step === 0" key="0" class="step">
            <p class="question">무엇을<br/>달성하고 싶으세요?</p>
            <input
              ref="titleInput"
              v-model="form.title"
              type="text"
              placeholder="목표를 입력하세요"
              class="big-input"
              @keyup.enter="next"
            />
            <p class="input-hint">예: 신규 고객 100명 유치, 이직 준비 완료, 체중 10kg 감량</p>
          </div>

          <!-- Step 1: 왜? (새로 추가) -->
          <div v-else-if="step === 1" key="1" class="step">
            <p class="question">왜 이 목표를<br/>달성하고 싶으세요?</p>
            <p class="step-sub">힘들 때 다시 읽어볼 동기예요</p>
            <textarea
              v-model="form.description"
              placeholder="이 목표가 나에게 중요한 이유..."
              class="big-textarea"
              rows="3"
              @keyup.ctrl.enter="next"
            ></textarea>
            <button class="skip-link" @click="form.description = ''; next()">
              건너뛰기 →
            </button>
          </div>

          <!-- Step 2: 얼마나? -->
          <div v-else-if="step === 2" key="2" class="step">
            <p class="question">얼마나<br/>달성하고 싶으세요?</p>
            <div class="value-row">
              <input
                v-model.number="form.value"
                type="number"
                placeholder="0"
                class="huge-input"
                @keyup.enter="next"
              />
              <div class="unit-picker">
                <button 
                  v-for="u in units" 
                  :key="u" 
                  :class="{ active: form.unit === u }"
                  @click="form.unit = u"
                >{{ u }}</button>
              </div>
            </div>
            <div class="type-row">
              <button 
                v-for="t in types" 
                :key="t.value"
                :class="{ active: form.goal_type === t.value }"
                @click="selectType(t.value)"
              >{{ t.label }}</button>
            </div>
            
            <!-- 현재 값 (0부터 시작 안 할 수도 있으니) -->
            <div class="current-value-row" v-if="form.value > 0">
              <span class="current-label">현재 어디까지 와있나요?</span>
              <div class="current-input-group">
                <input 
                  v-model.number="form.current_value" 
                  type="number" 
                  placeholder="0"
                  class="current-input"
                />
                <span class="current-unit">{{ form.unit }}</span>
              </div>
            </div>
          </div>

          <!-- Step 3: 언제까지? -->
          <div v-else-if="step === 3" key="3" class="step">
            <p class="question">언제까지<br/>달성할까요?</p>
            <div class="period-grid">
              <button 
                v-for="p in periods" 
                :key="p.type"
                :class="{ active: selectedPeriod === p.type }"
                @click="applyPeriod(p.type)"
              >
                <span class="period-label">{{ p.label }}</span>
                <span class="period-date">{{ getPeriodEndDate(p.type) }}</span>
              </button>
            </div>
            <div class="custom-date">
              <span>직접 선택</span>
              <input v-model="form.end_date" type="date" :min="form.start_date" @change="selectedPeriod = 'custom'" />
            </div>
          </div>

          <!-- Step 4: 확인 -->
          <div v-else-if="step === 4" key="4" class="step confirm">
            <p class="question">준비됐나요? 🎯</p>
            <div class="summary">
              <div class="summary-title">{{ form.title }}</div>
              
              <!-- 동기 표시 -->
              <div class="summary-why" v-if="form.description">
                <span class="why-label">💡 왜?</span>
                <p>{{ form.description }}</p>
              </div>

              <div class="summary-value">
                <span class="num">{{ formatValue(form.value) }}</span>
                <span class="unit">{{ form.unit }}</span>
              </div>
              
              <!-- 시작점 표시 -->
              <div class="summary-start" v-if="form.current_value > 0">
                <span>현재 {{ formatValue(form.current_value) }}{{ form.unit }}에서 시작</span>
              </div>

              <div class="summary-period">
                {{ formatPeriod() }}
              </div>

              <!-- 일일 목표 계산 -->
              <div class="summary-daily" v-if="dailyTarget">
                <span class="daily-label">하루 평균</span>
                <span class="daily-value">{{ dailyTarget }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- 수정 모드: 한 화면에 모든 필드 -->
      <div class="content edit-content" v-else>
        <div class="edit-form">
          <div class="field">
            <label>목표</label>
            <input v-model="form.title" type="text" placeholder="목표를 입력하세요" />
          </div>

          <div class="field">
            <label>왜 이 목표를?</label>
            <textarea v-model="form.description" placeholder="동기, 이유, 다짐..." rows="2"></textarea>
          </div>

          <div class="field-row">
            <div class="field">
              <label>현재 값</label>
              <input v-model.number="form.current_value" type="number" placeholder="0" />
            </div>
            <div class="field">
              <label>목표값</label>
              <input v-model.number="form.value" type="number" placeholder="0" />
            </div>
          </div>

          <div class="field-row">
            <div class="field">
              <label>유형</label>
              <select v-model="form.goal_type" @change="onTypeChange">
                <option value="count">건수</option>
                <option value="amount">금액</option>
                <option value="rate">비율</option>
              </select>
            </div>
            <div class="field">
              <label>단위</label>
              <select v-model="form.unit">
                <option v-for="u in units" :key="u" :value="u">{{ u }}</option>
              </select>
            </div>
          </div>

          <div class="field-row">
            <div class="field">
              <label>시작일</label>
              <input v-model="form.start_date" type="date" />
            </div>
            <div class="field">
              <label>종료일</label>
              <input v-model="form.end_date" type="date" :min="form.start_date" />
            </div>
          </div>

          <!-- 진행률 시각화 (수정 모드) -->
          <div class="edit-progress" v-if="isEdit && form.value > 0">
            <label>진행률</label>
            <div class="edit-progress-bar-bg">
              <div class="edit-progress-bar-fill" :style="{ width: editPercent + '%' }"></div>
            </div>
            <span class="edit-percent">{{ editPercent }}%</span>
          </div>

          <div class="field">
            <label>상태</label>
            <div class="status-grid">
              <button 
                v-for="s in statuses" 
                :key="s.value"
                :class="['status-btn', s.value, { active: form.status === s.value }]"
                @click="form.status = s.value"
              >
                <i :class="s.icon"></i>
                <span>{{ s.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 하단 버튼: 신규 모드 -->
      <footer v-if="!isEdit">
        <button v-if="step > 0" class="back" @click="step--">
          <i class="ph ph-arrow-left"></i>
        </button>
        <div v-else></div>
        
        <button 
          v-if="step < totalSteps - 1" 
          class="next" 
          :disabled="!canNext"
          @click="next"
        >
          다음
          <i class="ph ph-arrow-right"></i>
        </button>
        <button 
          v-else 
          class="save" 
          :disabled="saving"
          @click="save"
        >
          <i v-if="saving" class="ph ph-spinner fa-spin"></i>
          시작하기 🔥
        </button>
      </footer>

      <!-- 하단 버튼: 수정 모드 -->
      <footer v-else class="edit-footer">
        <button class="cancel" @click="editDialogSwitch(false)">취소</button>
        <button class="save" :disabled="!isValid || saving" @click="save">
          <i v-if="saving" class="ph ph-spinner fa-spin"></i>
          저장
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, inject, defineProps } from 'vue'

const props = defineProps({
  goal: Object,
  ownerType: { type: String, default: 'member' },
  ownerIdx: { type: [Number, String], default: null },
  editDialogSwitch: { type: Function, required: true },
  reloadForSync: { type: Function, default: () => {} }
})

const sql_query = inject('sql_query')
const getMbid = inject('getMbid')

const saving = ref(false)
const step = ref(0)
const titleInput = ref(null)
const selectedPeriod = ref('month')
const totalSteps = 5 // 0~4

const isEdit = computed(() => !!props.goal?.idx)

const form = reactive({
  title: props.goal?.title || '',
  description: props.goal?.description || '',
  goal_type: props.goal?.goal_type || 'count',
  value: props.goal?.value || null,
  current_value: props.goal?.current_value || 0,
  unit: props.goal?.unit || '건',
  start_date: props.goal?.start_date?.substring(0, 10) || today(),
  end_date: props.goal?.end_date?.substring(0, 10) || endOfMonth(),
  status: props.goal?.status || 'active'
})

const types = [
  { value: 'count', label: '건수', units: ['건', '명', '회', '개'] },
  { value: 'amount', label: '금액', units: ['원', '만원'] },
  { value: 'rate', label: '비율', units: ['%'] }
]

const statuses = [
  { value: 'active', label: '진행중', icon: 'ph ph-play' },
  { value: 'completed', label: '완료', icon: 'ph ph-check' },
  { value: 'paused', label: '일시정지', icon: 'ph ph-pause' },
  { value: 'cancelled', label: '취소', icon: 'ph ph-x' }
]

const periods = [
  { type: 'week', label: '이번 주' },
  { type: 'month', label: '이번 달' },
  { type: 'quarter', label: '이번 분기' },
  { type: 'year', label: '올해' }
]

const units = computed(() => {
  const t = types.find(x => x.value === form.goal_type)
  return t ? t.units : ['건']
})

const canNext = computed(() => {
  if (step.value === 0) return form.title.trim().length > 0
  if (step.value === 1) return true // description은 선택
  if (step.value === 2) return form.value > 0
  if (step.value === 3) return form.end_date && form.start_date <= form.end_date
  return true
})

const isValid = computed(() => {
  return form.title.trim() && form.value > 0 && form.start_date && form.end_date && form.start_date <= form.end_date
})

const editPercent = computed(() => {
  if (!form.value || form.value === 0) return 0
  return Math.min(100, Math.round(((form.current_value || 0) / form.value) * 100))
})

// 일일 목표 계산
const dailyTarget = computed(() => {
  if (!form.value || !form.start_date || !form.end_date) return null
  const s = new Date(form.start_date)
  const e = new Date(form.end_date)
  const days = Math.max(1, Math.ceil((e - s) / (1000 * 60 * 60 * 24)))
  const remaining = form.value - (form.current_value || 0)
  if (remaining <= 0) return null
  const daily = remaining / days
  
  if (form.goal_type === 'amount') {
    if (form.unit === '만원') return `약 ${Math.ceil(daily).toLocaleString()}만원`
    return `약 ${Math.ceil(daily).toLocaleString()}원`
  }
  
  if (daily < 1) {
    const daysPerOne = Math.round(1 / daily)
    return `${daysPerOne}일에 1${form.unit}`
  }
  return `약 ${daily.toFixed(1)}${form.unit}`
})

onMounted(() => {
  if (!isEdit.value) {
    nextTick(() => titleInput.value?.focus())
  }
})

function next() {
  if (canNext.value && step.value < totalSteps - 1) step.value++
}

function selectType(type) {
  form.goal_type = type
  const t = types.find(x => x.value === type)
  if (t) form.unit = t.units[0]
}

function onTypeChange() {
  const t = types.find(x => x.value === form.goal_type)
  if (t) form.unit = t.units[0]
}

function today() {
  return formatDate(new Date())
}

function endOfMonth() {
  const d = new Date()
  return formatDate(new Date(d.getFullYear(), d.getMonth() + 1, 0))
}

function formatDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getPeriodEndDate(type) {
  const d = new Date()
  let end
  if (type === 'week') {
    end = new Date(d)
    end.setDate(d.getDate() + (7 - d.getDay()))
  } else if (type === 'month') {
    end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  } else if (type === 'quarter') {
    const q = Math.floor(d.getMonth() / 3)
    end = new Date(d.getFullYear(), (q + 1) * 3, 0)
  } else {
    end = new Date(d.getFullYear(), 11, 31)
  }
  return `${end.getMonth() + 1}/${end.getDate()}`
}

function applyPeriod(type) {
  selectedPeriod.value = type
  const d = new Date()
  form.start_date = today()
  
  if (type === 'week') {
    const end = new Date(d)
    end.setDate(d.getDate() + (7 - d.getDay()))
    form.end_date = formatDate(end)
  } else if (type === 'month') {
    form.end_date = formatDate(new Date(d.getFullYear(), d.getMonth() + 1, 0))
  } else if (type === 'quarter') {
    const q = Math.floor(d.getMonth() / 3)
    form.end_date = formatDate(new Date(d.getFullYear(), (q + 1) * 3, 0))
  } else {
    form.end_date = `${d.getFullYear()}-12-31`
  }
}

function formatValue(val) {
  return Number(val || 0).toLocaleString()
}

function formatPeriod() {
  if (!form.start_date || !form.end_date) return ''
  const s = new Date(form.start_date)
  const e = new Date(form.end_date)
  const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1
  return `${s.getMonth() + 1}/${s.getDate()} ~ ${e.getMonth() + 1}/${e.getDate()} (${days}일)`
}

async function save() {
  if (!isValid.value) return
  saving.value = true
  try {
    const mb_id = getMbid()
    if (isEdit.value) {
      await sql_query(`UPDATE goals SET 
        title = '${form.title.replace(/'/g, "''")}',
        description = '${(form.description || '').replace(/'/g, "''")}',
        goal_type = '${form.goal_type}',
        value = ${form.value},
        current_value = ${form.current_value || 0},
        unit = '${form.unit}',
        start_date = '${form.start_date}',
        end_date = '${form.end_date}',
        status = '${form.status}'
        WHERE idx = ${props.goal.idx}`)
    } else {
      await sql_query(`INSERT INTO goals SET
        mb_id = '${mb_id}',
        owner_type = '${props.ownerType}',
        owner_idx = ${props.ownerIdx || 'NULL'},
        title = '${form.title.replace(/'/g, "''")}',
        description = '${(form.description || '').replace(/'/g, "''")}',
        goal_type = '${form.goal_type}',
        value = ${form.value},
        current_value = ${form.current_value || 0},
        unit = '${form.unit}',
        start_date = '${form.start_date}',
        end_date = '${form.end_date}',
        status = 'active'`)
    }
    props.reloadForSync()
    window.dispatchEvent(new Event('goals-updated'))
    props.editDialogSwitch(false)
  } catch (e) {
    alert('저장 실패')
    console.error(e)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
#dialog-goal-edit {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-head);
  background: var(--color-surface-deep);
  backdrop-filter: blur(var(--effect-blur));
  padding: 16px;
  box-sizing: border-box;
}

.cls {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.paper {
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  background: var(--color-background);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

/* 진행 바 */
.progress-bar {
  height: 4px;
  background: var(--color-surface-reverse);
  flex-shrink: 0;
}

.progress-bar .fill {
  height: 100%;
  background: var(--color-brand);
  transition: width 0.3s ease;
}

/* 수정 모드 헤더 */
.edit-header {
  padding: 24px 24px 0;
  flex-shrink: 0;
}

.edit-header h2 {
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin: 0;
}

/* 닫기 */
.close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
  z-index: 10;
  transition: transform 0.2s;
}

.close:hover {
  transform: scale(1.1);
}

/* 컨텐츠 */
.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 60px 32px 32px;
  overflow-y: auto;
}

.content.edit-content {
  padding: 24px;
}

/* 애니메이션 */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* 스텝 */
.step {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.question {
  font-size: var(--font-size-2xl);
  font-weight: 800;
  line-height: 1.3;
  margin: 0 0 32px;
  letter-spacing: -1px;
}

.step-sub {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: -20px 0 24px;
}

.input-hint {
  margin: 12px 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  line-height: 1.5;
}

/* 큰 입력 */
.big-input {
  width: 100%;
  padding: 16px 0;
  border: none;
  border-bottom: 3px solid var(--color-surface-reverse);
  background: none;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.big-input:focus {
  border-color: var(--color-brand);
}

.big-input::placeholder {
  color: var(--color-text-third);
}

/* 큰 텍스트에리어 */
.big-textarea {
  width: 100%;
  padding: 16px;
  border: 2px solid var(--color-surface-reverse);
  border-radius: 14px;
  background: var(--color-background);
  font-size: var(--font-size-base);
  font-weight: 500;
  color: var(--color-text-primary);
  outline: none;
  resize: none;
  line-height: 1.5;
  transition: border-color 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.big-textarea:focus {
  border-color: var(--color-brand);
}

.big-textarea::placeholder {
  color: var(--color-text-third);
}

.skip-link {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 0;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  text-align: left;
  transition: color 0.15s;
}

.skip-link:hover {
  color: var(--color-brand);
}

/* 값 입력 */
.value-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
}

.huge-input {
  flex: 1;
  width: 100%;
  padding: 8px 0;
  border: none;
  border-bottom: 3px solid var(--color-surface-reverse);
  background: none;
  font-size: 48px;
  font-weight: 800;
  color: var(--color-brand);
  outline: none;
  text-align: left;
  box-sizing: border-box;
}

.huge-input:focus {
  border-color: var(--color-brand);
}

.huge-input::placeholder {
  color: var(--color-text-third);
}

.unit-picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.unit-picker button {
  padding: 8px 16px;
  border: 2px solid var(--color-surface-reverse);
  background: none;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.unit-picker button.active {
  border-color: var(--color-brand);
  background: var(--color-brand);
  color: #000;
}

.type-row {
  display: flex;
  gap: 8px;
}

.type-row button {
  flex: 1;
  padding: 12px;
  border: 2px solid var(--color-surface-reverse);
  background: none;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.type-row button.active {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

/* 현재 값 입력 */
.current-value-row {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-surface-reverse);
}

.current-label {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-bottom: 10px;
}

.current-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-input {
  width: 120px;
  padding: 10px 14px;
  border: 2px solid var(--color-surface-reverse);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: 600;
  box-sizing: border-box;
}

.current-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.current-unit {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 600;
}

/* 기간 선택 */
.period-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}

.period-grid button {
  padding: 20px 16px;
  border: 2px solid var(--color-surface-reverse);
  background: none;
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
}

.period-grid button:hover {
  border-color: var(--color-text-secondary);
}

.period-grid button.active {
  border-color: var(--color-brand);
  background: rgba(255, 165, 0, 0.1);
}

.period-label {
  display: block;
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.period-date {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.custom-date {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--color-surface-reverse);
}

.custom-date span {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.custom-date input {
  flex: 1;
  padding: 12px;
  border: 2px solid var(--color-surface-reverse);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  box-sizing: border-box;
}

.custom-date input:focus {
  outline: none;
  border-color: var(--color-brand);
}

/* 확인 화면 */
.step.confirm {
  justify-content: center;
  text-align: center;
}

.step.confirm .question {
  font-size: var(--font-size-xl);
  color: var(--color-text-secondary);
  margin-bottom: 24px;
}

.summary {
  background: var(--color-background-light);
  border-radius: 20px;
  padding: 28px 24px;
}

.summary-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: 12px;
  word-break: keep-all;
}

.summary-why {
  padding: 12px 16px;
  background: rgba(255, 165, 0, 0.08);
  border-radius: 12px;
  margin-bottom: 16px;
  text-align: left;
}

.summary-why .why-label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-brand);
}

.summary-why p {
  margin: 6px 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  line-height: 1.4;
}

.summary-value {
  margin-bottom: 8px;
}

.summary-value .num {
  font-size: 48px;
  font-weight: 900;
  color: var(--color-brand);
}

.summary-value .unit {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-brand);
  margin-left: 4px;
}

.summary-start {
  margin-bottom: 8px;
}

.summary-start span {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  background: var(--color-surface-reverse);
  padding: 4px 10px;
  border-radius: 8px;
}

.summary-period {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-bottom: 12px;
}

.summary-daily {
  padding: 12px;
  background: rgba(34, 197, 94, 0.08);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.daily-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.daily-value {
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: #22c55e;
}

/* ==================== */
/* 수정 모드 스타일 */
/* ==================== */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid var(--color-surface-reverse);
  border-radius: 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  box-sizing: border-box;
  transition: border-color 0.2s;
  font-family: inherit;
}

.field textarea {
  resize: none;
  line-height: 1.4;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.field select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* 수정 모드 진행률 */
.edit-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-progress label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.edit-progress-bar-bg {
  height: 8px;
  background: var(--color-surface-reverse);
  border-radius: 4px;
  overflow: hidden;
}

.edit-progress-bar-fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.edit-percent {
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--color-brand);
}

/* 상태 그리드 */
.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.status-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 12px;
  border: 2px solid var(--color-surface-reverse);
  background: none;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.status-btn i {
  font-size: var(--font-size-base);
}

.status-btn:hover {
  border-color: var(--color-text-secondary);
}

.status-btn.active.active {
  border-color: var(--color-brand);
  background: rgba(255, 165, 0, 0.1);
  color: var(--color-brand);
}

.status-btn.completed.active {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.status-btn.paused.active {
  border-color: var(--color-text-secondary);
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.status-btn.cancelled.active {
  border-color: var(--color-special-warning);
  background: rgba(218, 47, 71, 0.1);
  color: var(--color-special-warning);
}

/* 푸터 */
footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 24px;
  flex-shrink: 0;
}

footer.edit-footer {
  gap: 12px;
}

footer button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 14px;
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.back {
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.back:hover {
  background: var(--color-background-light);
}

.cancel {
  flex: 1;
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.cancel:hover {
  background: var(--color-background-light);
}

.next,
.save {
  background: var(--color-brand);
  color: #000;
}

.edit-footer .save {
  flex: 1;
}

.next:hover:not(:disabled),
.save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(255, 165, 0, 0.4);
}

.next:disabled,
.save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 모바일 */
@media (max-width: 768px) {
  .paper {
    max-height: 95vh;
    border-radius: 24px 24px 0 0;
  }
  
  .question {
    font-size: var(--font-size-xl);
  }
  
  .huge-input {
    font-size: 40px;
  }
  
  .status-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>